/**
 * SyllabusEditor — 3-level hierarchical editor
 *
 * Level 1 — Main Section   : "I. GENERAL KNOWLEDGE"
 * Level 2 — Sub-Section    : "(i) HISTORY (5 Marks)"  + linked podcast
 * Level 3 — Topic Items    : "KERALA", "INDIA", "WORLD" + content + episode link
 *
 * Episode picker is enabled only after a podcast is selected for that sub-section.
 * Episodes are fetched lazily via GET /podcast/:id/episode?mode=dropdown → [{ label, value }]
 *
 * Props:
 *   rows                — [{ _id, heading, content, subSections[] }]
 *   onChange            — (rows) => void
 *   podcastOptions      — [{ label, value }]
 *   podcastsLoading     — bool
 *   podcastsError       — bool
 *   suggestedPodcastIds — string[] (from mark distribution — starred first)
 */
import { useContext } from 'react';
import { useQuery } from 'react-query';
import { FetchContext } from 'context/FetchContext';
import PodcastDropdown from 'components/molecules/PodcastDropdown';

// react-select portal props — prevents menu clipping in overflow containers
const PORTAL_PROPS = {
  menuPortalTarget: typeof document !== 'undefined' ? document.body : null,
  menuPosition: 'fixed',
  styles: {
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    control: (base) => ({ ...base, minHeight: 36, fontSize: 13 }),
    valueContainer: (base) => ({ ...base, padding: '0 8px' }),
    indicatorsContainer: (base) => ({ ...base, height: 36 }),
  },
};

// ── Episode options hook ───────────────────────────────────────────────────────
function useEpisodeOptions(podcastId) {
  const { privateFetch } = useContext(FetchContext);
  return useQuery(
    ['syllabus-episode-options', podcastId],
    async () => {
      // includeUnpublished=true — exam-info must show all episodes, published or not
      const res = await privateFetch.get(
        `/v1/admin/podcast/${podcastId}/episode?mode=dropdown&includeUnpublished=true`
      );
      const raw = res?.data?.data;
      if (!Array.isArray(raw)) return [];
      if (raw.length && raw[0].label !== undefined) return raw;
      return raw.map((ep) => ({ label: ep.name || ep.title || ep._id, value: ep._id }));
    },
    { enabled: !!podcastId, staleTime: 2 * 60 * 1000, keepPreviousData: true }
  );
}

// ── Level 3: Item card ────────────────────────────────────────────────────────
function ItemCard({ item, iIdx, podcastId, onUpdate, onRemove }) {
  const { data: episodeOptions = [], isLoading: episodesLoading } = useEpisodeOptions(
    podcastId || null
  );

  return (
    <div style={styles.itemCard}>
      <div style={styles.itemHeader}>
        <span style={styles.itemCount}>↳ Topic {iIdx + 1}</span>
        <button onClick={onRemove} style={styles.removeBtn} title="Remove topic">
          ✕
        </button>
      </div>

      {/* Label + Episode — same row */}
      <div style={styles.itemTopRow}>
        <div style={{ flex: 1, minWidth: 120 }}>
          <label style={styles.fieldLabel}>Label *</label>
          <input
            value={item.label}
            onChange={(e) => onUpdate('label', e.target.value)}
            placeholder="e.g. KERALA"
            style={styles.input}
          />
        </div>

        <div style={{ flex: 2, minWidth: 200 }}>
          <label style={styles.fieldLabel}>
            Link Episode
            {!podcastId && (
              <span style={{ ...styles.optional, color: '#f59e0b' }}>
                {' '}
                — select podcast in sub-section first
              </span>
            )}
          </label>
          {podcastId ? (
            <select
              value={item.episodeId || ''}
              onChange={(e) => onUpdate('episodeId', e.target.value)}
              style={styles.select}
              disabled={episodesLoading}
            >
              {episodesLoading ? (
                <option disabled value="">
                  Loading episodes…
                </option>
              ) : (
                <>
                  <option value="">— None —</option>
                  {episodeOptions.map((ep) => (
                    <option key={ep.value} value={ep.value}>
                      {ep.label}
                    </option>
                  ))}
                </>
              )}
            </select>
          ) : (
            <input
              disabled
              placeholder="Select podcast above first"
              style={{ ...styles.input, background: '#f5f5f5', color: '#aaa' }}
            />
          )}
        </div>
      </div>

      {/* Content */}
      <div style={{ marginTop: 8 }}>
        <label style={styles.fieldLabel}>
          Content <span style={styles.optional}>(syllabus text for this topic)</span>
        </label>
        <textarea
          value={item.content}
          onChange={(e) => onUpdate('content', e.target.value)}
          placeholder="e.g. Arrival of Europeans – Contributions of Europeans – History of Travancore…"
          rows={3}
          style={{ ...styles.input, resize: 'vertical', lineHeight: 1.6 }}
        />
      </div>
    </div>
  );
}

// ── Level 2: Sub-section card ─────────────────────────────────────────────────
function SubSectionCard({
  sub,
  sIdx,
  podcastOptions,
  podcastsLoading,
  podcastsError,
  suggestedPodcastIds,
  onUpdate,
  onRemove,
}) {
  // Star suggested podcasts at top
  const sortedPodcastOptions =
    suggestedPodcastIds && suggestedPodcastIds.length
      ? [
          ...podcastOptions
            .filter((o) => suggestedPodcastIds.includes(o.value))
            .map((o) => ({ ...o, label: `⭐ ${o.label}` })),
          ...podcastOptions.filter((o) => !suggestedPodcastIds.includes(o.value)),
        ]
      : podcastOptions;

  const handlePodcastChange = (e) => {
    const newPodcastId = e.target.value;
    // Clear all episodeIds when podcast changes
    const clearedItems = (sub.items || []).map((item) => ({ ...item, episodeId: '' }));
    onUpdate({ podcastId: newPodcastId, items: clearedItems });
  };

  const addItem = () =>
    onUpdate({ items: [...(sub.items || []), { label: '', content: '', episodeId: '' }] });

  const updateItem = (iIdx, field, value) =>
    onUpdate({
      items: (sub.items || []).map((item, j) => (j === iIdx ? { ...item, [field]: value } : item)),
    });

  const removeItem = (iIdx) => onUpdate({ items: (sub.items || []).filter((_, j) => j !== iIdx) });

  return (
    <div style={styles.subSectionCard}>
      <div style={styles.subSectionHeader}>
        <span style={styles.subSectionLabel}>Sub-section {sIdx + 1}</span>
        <button onClick={onRemove} style={styles.removeSubBtn}>
          ✕ Remove
        </button>
      </div>

      {/* Sub-section heading */}
      <div style={styles.fieldGroup}>
        <label style={styles.fieldLabel}>
          Sub-section Heading *<span style={styles.optional}> e.g. (i) HISTORY (5 Marks)</span>
        </label>
        <input
          value={sub.heading}
          onChange={(e) => onUpdate({ heading: e.target.value })}
          placeholder="e.g. (i) HISTORY (5 Marks)"
          style={styles.input}
        />
      </div>

      {/* Optional overview */}
      <div style={styles.fieldGroup}>
        <label style={styles.fieldLabel}>
          Overview <span style={styles.optional}>(optional intro text for this sub-section)</span>
        </label>
        <textarea
          value={sub.content}
          onChange={(e) => onUpdate({ content: e.target.value })}
          placeholder="Optional overview…"
          rows={2}
          style={{ ...styles.input, resize: 'vertical' }}
        />
      </div>

      {/* Podcast picker */}
      <div style={{ ...styles.fieldGroup, maxWidth: 480 }}>
        <PodcastDropdown
          version="v2"
          label="Link a Podcast (optional)"
          value={sub.podcastId}
          options={sortedPodcastOptions}
          isLoading={podcastsLoading}
          isError={podcastsError}
          placeholder="— None —"
          handleChange={handlePodcastChange}
          {...PORTAL_PROPS}
        />
        {sub.podcastId && (
          <p style={styles.hint}>Episodes below will be loaded from this podcast.</p>
        )}
        {suggestedPodcastIds.length > 0 && (
          <p style={styles.hint}>⭐ = already linked in Mark Distribution</p>
        )}
      </div>

      {/* Topic items */}
      <div style={styles.itemsArea}>
        <div style={styles.itemsHeader}>
          <span style={styles.itemsTitle}>Topics</span>
          <span style={styles.itemsSubtitle}>
            Each topic can have its own content and episode link
          </span>
        </div>

        {(!sub.items || sub.items.length === 0) && (
          <p style={styles.emptyHint}>
            No topics yet. Click "+ Add Topic" to add one (e.g. KERALA, INDIA, WORLD).
          </p>
        )}

        {(sub.items || []).map((item, iIdx) => (
          <ItemCard
            key={iIdx}
            item={item}
            iIdx={iIdx}
            podcastId={sub.podcastId}
            onUpdate={(field, value) => updateItem(iIdx, field, value)}
            onRemove={() => removeItem(iIdx)}
          />
        ))}

        <button onClick={addItem} style={styles.addItemBtn}>
          + Add Topic
        </button>
      </div>
    </div>
  );
}

// ── Level 1: Main section card ────────────────────────────────────────────────
function MainSectionCard({
  row,
  idx,
  podcastOptions,
  podcastsLoading,
  podcastsError,
  suggestedPodcastIds,
  onUpdate,
  onRemove,
}) {
  const addSubSection = () =>
    onUpdate({
      subSections: [
        ...(row.subSections || []),
        { heading: '', content: '', podcastId: '', items: [] },
      ],
    });

  const updateSubSection = (sIdx, partial) =>
    onUpdate({
      subSections: (row.subSections || []).map((s, i) => (i === sIdx ? { ...s, ...partial } : s)),
    });

  const removeSubSection = (sIdx) =>
    onUpdate({ subSections: (row.subSections || []).filter((_, i) => i !== sIdx) });

  return (
    <div style={styles.mainSectionCard}>
      {/* Main section header */}
      <div style={styles.mainHeader}>
        <span style={styles.mainLabel}>Section {idx + 1}</span>
        <button onClick={onRemove} style={styles.removeMainBtn}>
          ✕ Remove Section
        </button>
      </div>

      {/* Main heading */}
      <div style={styles.fieldGroup}>
        <label style={styles.fieldLabel}>
          Main Heading *<span style={styles.optional}> e.g. I. GENERAL KNOWLEDGE</span>
        </label>
        <input
          value={row.heading}
          onChange={(e) => onUpdate({ heading: e.target.value })}
          placeholder="e.g. I. GENERAL KNOWLEDGE"
          style={{ ...styles.input, fontWeight: 600 }}
        />
      </div>

      {/* Optional section intro */}
      <div style={styles.fieldGroup}>
        <label style={styles.fieldLabel}>
          Section Intro <span style={styles.optional}>(optional)</span>
        </label>
        <textarea
          value={row.content}
          onChange={(e) => onUpdate({ content: e.target.value })}
          placeholder="Optional intro text for this main section…"
          rows={2}
          style={{ ...styles.input, resize: 'vertical' }}
        />
      </div>

      {/* Sub-sections */}
      <div style={styles.subSectionsArea}>
        <div style={styles.subSectionsHeader}>
          <span style={styles.subSectionsTitle}>Sub-sections</span>
          <span style={styles.itemsSubtitle}>e.g. (i) HISTORY, (ii) GEOGRAPHY</span>
        </div>

        {(!row.subSections || row.subSections.length === 0) && (
          <p style={styles.emptyHint}>No sub-sections yet. Click "+ Add Sub-section" to add one.</p>
        )}

        {(row.subSections || []).map((sub, sIdx) => (
          <SubSectionCard
            key={sIdx}
            sub={sub}
            sIdx={sIdx}
            podcastOptions={podcastOptions}
            podcastsLoading={podcastsLoading}
            podcastsError={podcastsError}
            suggestedPodcastIds={suggestedPodcastIds}
            onUpdate={(partial) => updateSubSection(sIdx, partial)}
            onRemove={() => removeSubSection(sIdx)}
          />
        ))}

        <button onClick={addSubSection} style={styles.addSubBtn}>
          + Add Sub-section
        </button>
      </div>
    </div>
  );
}

// ── Root editor ───────────────────────────────────────────────────────────────
export default function SyllabusEditor({
  rows,
  onChange,
  podcastOptions = [],
  podcastsLoading = false,
  podcastsError = false,
  suggestedPodcastIds = [],
}) {
  const addSection = () => onChange([...rows, { heading: '', content: '', subSections: [] }]);

  // Each card calls onUpdate with a partial object merged into the row
  const update = (idx, partial) =>
    onChange(rows.map((r, i) => (i === idx ? { ...r, ...partial } : r)));

  const remove = (idx) => onChange(rows.filter((_, i) => i !== idx));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {rows.length === 0 && (
        <p className="text-muted" style={{ fontSize: 13 }}>
          No sections yet. Click "+ Add Section" below.
        </p>
      )}

      {rows.map((row, idx) => (
        <MainSectionCard
          key={idx}
          row={row}
          idx={idx}
          onUpdate={(partial) => update(idx, partial)}
          onRemove={() => remove(idx)}
          podcastOptions={podcastOptions}
          podcastsLoading={podcastsLoading}
          podcastsError={podcastsError}
          suggestedPodcastIds={suggestedPodcastIds}
        />
      ))}

      <button onClick={addSection} style={styles.addSectionBtn}>
        + Add Section
      </button>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────
const styles = {
  // Level 1 — Main section
  mainSectionCard: {
    background: '#fff',
    border: '2px solid #c7d2fe',
    borderRadius: 10,
    overflow: 'visible',
  },
  mainHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 16px',
    background: '#eef2ff',
    borderBottom: '1px solid #c7d2fe',
    borderRadius: '8px 8px 0 0',
  },
  mainLabel: {
    fontWeight: 700,
    fontSize: 13,
    color: '#3730a3',
  },
  removeMainBtn: {
    color: '#c62828',
    border: '1px solid #ef9a9a',
    background: '#fff5f5',
    borderRadius: 4,
    cursor: 'pointer',
    fontSize: 12,
    padding: '3px 10px',
  },

  // Level 2 — Sub-section
  subSectionsArea: {
    margin: '12px 16px 16px',
    border: '1px solid #e0e7ff',
    borderRadius: 6,
    background: '#f5f7ff',
    padding: '12px 14px',
  },
  subSectionsHeader: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 10,
    marginBottom: 10,
  },
  subSectionsTitle: {
    fontWeight: 700,
    fontSize: 13,
    color: '#3730a3',
  },
  subSectionCard: {
    background: '#fff',
    border: '1px solid #dde1e7',
    borderRadius: 8,
    marginBottom: 10,
    overflow: 'visible',
  },
  subSectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 14px',
    background: '#f5f7fa',
    borderBottom: '1px solid #e8eaed',
    borderRadius: '8px 8px 0 0',
  },
  subSectionLabel: {
    fontWeight: 600,
    fontSize: 12,
    color: '#374151',
  },
  removeSubBtn: {
    color: '#c62828',
    border: '1px solid #ef9a9a',
    background: '#fff5f5',
    borderRadius: 4,
    cursor: 'pointer',
    fontSize: 11,
    padding: '2px 8px',
  },
  addSubBtn: {
    marginTop: 4,
    padding: '6px 16px',
    borderRadius: 4,
    border: '1px dashed #818cf8',
    background: '#eef2ff',
    color: '#3730a3',
    cursor: 'pointer',
    fontSize: 12,
    fontWeight: 600,
  },

  // Level 3 — Items
  itemsArea: {
    margin: '10px 14px 14px',
    border: '1px solid #eef0f4',
    borderRadius: 6,
    background: '#fafbfc',
    padding: '10px 12px',
  },
  itemsHeader: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 10,
    marginBottom: 8,
  },
  itemsTitle: {
    fontWeight: 700,
    fontSize: 12,
    color: '#333',
  },
  itemCard: {
    background: '#fff',
    border: '1px solid #e0e4ea',
    borderRadius: 6,
    padding: '10px 12px 12px',
    marginBottom: 8,
  },
  itemHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  itemCount: {
    fontSize: 11,
    fontWeight: 600,
    color: '#6b7280',
  },
  itemTopRow: {
    display: 'flex',
    gap: 12,
    flexWrap: 'wrap',
    alignItems: 'flex-end',
  },
  removeBtn: {
    color: '#e53935',
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    fontSize: 14,
    padding: '2px 4px',
  },
  addItemBtn: {
    marginTop: 4,
    padding: '5px 14px',
    borderRadius: 4,
    border: '1px dashed #60a5fa',
    background: '#eff6ff',
    color: '#1d4ed8',
    cursor: 'pointer',
    fontSize: 12,
    fontWeight: 600,
  },

  // Shared
  fieldGroup: {
    padding: '10px 16px 0',
  },
  fieldLabel: {
    display: 'block',
    fontSize: 12,
    fontWeight: 600,
    color: '#444',
    marginBottom: 4,
  },
  optional: {
    fontWeight: 400,
    color: '#999',
    fontSize: 11,
  },
  hint: {
    margin: '4px 0 0',
    fontSize: 11,
    color: '#999',
  },
  input: {
    padding: '7px 10px',
    borderRadius: 4,
    border: '1px solid #d0d0d0',
    fontSize: 13,
    width: '100%',
    boxSizing: 'border-box',
    outline: 'none',
  },
  select: {
    width: '100%',
    padding: '7px 10px',
    borderRadius: 4,
    border: '1px solid #d0d0d0',
    fontSize: 13,
    outline: 'none',
    background: '#fff',
    cursor: 'pointer',
  },
  itemsSubtitle: {
    fontSize: 12,
    color: '#888',
  },
  emptyHint: {
    fontSize: 13,
    color: '#aaa',
    marginBottom: 10,
    fontStyle: 'italic',
  },
  addSectionBtn: {
    padding: '8px 20px',
    borderRadius: 4,
    border: '1px dashed #999',
    background: '#fff',
    cursor: 'pointer',
    alignSelf: 'flex-start',
    fontSize: 13,
    color: '#555',
  },
};
